import { I18n } from '@affine/i18n';
import { addSiblingImageBlocks } from '@blocksuite/affine/blocks/image';
import { EdgelessCRUDIdentifier } from '@blocksuite/affine/blocks/surface';
import {
  Bound,
  getCommonBoundWithRotation,
  type SerializedXYWH,
} from '@blocksuite/affine/global/gfx';
import { RefNodeSlotsProvider } from '@blocksuite/affine/inlines/reference';
import {
  type DocMode,
  NoteBlockModel,
  NoteDisplayMode,
} from '@blocksuite/affine/model';
import {
  getFirstBlockCommand,
  getLastBlockCommand,
  getSelectedBlocksCommand,
} from '@blocksuite/affine/shared/commands';
import type { ImageSelection } from '@blocksuite/affine/shared/selection';
import {
  DocModeProvider,
  EditPropsStore,
  NotificationProvider,
  TelemetryProvider,
} from '@blocksuite/affine/shared/services';
import { uploadBlob } from '@blocksuite/affine/shared/utils';
import {
  type BlockComponent,
  type BlockSelection,
  type BlockStdScope,
  type EditorHost,
  type TextSelection,
} from '@blocksuite/affine/std';
import { GfxControllerIdentifier } from '@blocksuite/affine/std/gfx';
import type { BlockModel, Store } from '@blocksuite/affine/store';
import {
  BlockIcon,
  EdgelessIcon,
  InsertBleowIcon as InsertBelowIcon,
  LinkedPageIcon,
  PageIcon,
} from '@blocksuite/icons/lit';
import type { TemplateResult } from 'lit';

import { insertFromMarkdown } from '../../utils';
import type { ChatMessage } from '../components/ai-chat-messages';
import { AIProvider, type AIUserInfo } from '../provider';
import { reportResponse } from '../utils/action-reporter';
import { insertBelow } from '../utils/editor-actions';
import { fetchImageToFile } from '../utils/image';
import { mergeStreamContent } from '../utils/stream-objects';
import {
  type AssistantTurnPayload,
  buildReusableAssistantTurnMarkdown,
  type ChatActionResult,
} from './assistant-turn-payload';

type Selections = {
  text?: TextSelection;
  blocks?: BlockSelection[];
  images?: ImageSelection[];
};

type AttachmentInsertResult = {
  requested: number;
  inserted: number;
};

const DEFAULT_EDGELESS_NOTE_WIDTH = 448;
const DEFAULT_EDGELESS_NOTE_HEIGHT = 72;
const EDGELESS_NOTE_SELECTION_GAP = 10;
export const AI_CHAT_BLOCK_WIDTH = 420;
export const AI_CHAT_BLOCK_HEIGHT = 420;

export type ChatAction = {
  icon: TemplateResult<1>;
  title: string;
  titleKey?: string;
  toast: string;
  toastKey?: string;
  showWhen: (host: EditorHost) => boolean;
  handler: (
    host: EditorHost,
    payload: AssistantTurnPayload,
    currentSelections: Selections
  ) => Promise<ChatActionResult>;
};

export function getChatActionTitle(action: ChatAction) {
  return action.titleKey ? I18n.t(action.titleKey) : action.title;
}

export function getChatActionToast(action: ChatAction) {
  return action.toastKey ? I18n.t(action.toastKey) : action.toast;
}

export async function queryHistoryMessages(
  workspaceId: string,
  forkSessionId: string,
  docId?: string
) {
  // Get fork session messages
  const histories = await AIProvider.histories?.chats(
    workspaceId,
    forkSessionId,
    docId
  );

  if (!histories || !histories.length) {
    return [];
  }

  return histories[0].messages;
}

// Construct user info with messages
export function constructUserInfoWithMessages(
  messages: ChatMessage[],
  userInfo: AIUserInfo | null
) {
  return messages.map(message => {
    const { role, streamObjects } = message;
    const isUser = role === 'user';
    const userInfoProps = isUser
      ? {
          userId: userInfo?.id,
          userName: userInfo?.name,
          avatarUrl: userInfo?.avatarUrl ?? undefined,
        }
      : {};
    return {
      ...message,
      ...userInfoProps,
      attachments: message.attachments ?? [],
      streamObjects: streamObjects || [],
    };
  });
}

function reusableMarkdown(payload: AssistantTurnPayload): string {
  return buildReusableAssistantTurnMarkdown(payload);
}

function resultWithAttachmentStatus(
  success: boolean,
  attachmentResult: AttachmentInsertResult | null,
  successToast: string
): ChatActionResult {
  if (!success) {
    return false;
  }
  if (
    !attachmentResult ||
    attachmentResult.requested === 0 ||
    attachmentResult.inserted === attachmentResult.requested
  ) {
    return true;
  }

  const failed = attachmentResult.requested - attachmentResult.inserted;
  const noun = failed === 1 ? 'attachment' : 'attachments';
  return {
    success: true,
    accent: 'warning',
    toast: `${successToast}; ${failed} ${noun} could not be inserted`,
  };
}

/**
 * Fetch attachment image URLs and insert them as image blocks after a target block.
 * Uses the same infrastructure as the inline AI panel (fetchImageToFile + addSiblingImageBlocks).
 */
async function insertAttachmentImages(
  host: EditorHost,
  attachments: string[],
  targetModel: BlockModel
): Promise<AttachmentInsertResult> {
  if (!attachments.length) {
    return { requested: 0, inserted: 0 };
  }

  const imageProxy = host.std.clipboard?.configs.get('imageProxy');
  const files: File[] = [];

  for (let i = 0; i < attachments.length; i++) {
    const file = await fetchImageToFile(
      attachments[i],
      `ai-image-${i}.png`,
      imageProxy
    );
    if (file) files.push(file);
  }

  if (files.length > 0) {
    await addSiblingImageBlocks(host.std, files, targetModel, 'after');
  }

  return { requested: attachments.length, inserted: files.length };
}

/**
 * Insert images into a note block (for "Save as doc" and "Add to edgeless as note").
 * Appends by default, or inserts at the requested child index.
 */
async function insertImagesIntoNote(
  host: EditorHost,
  attachments: string[],
  store: Store,
  noteId: string,
  index?: number
): Promise<AttachmentInsertResult> {
  if (!attachments.length) {
    return { requested: 0, inserted: 0 };
  }

  const imageProxy = host.std.clipboard?.configs.get('imageProxy');
  let inserted = 0;

  for (let i = 0; i < attachments.length; i++) {
    const file = await fetchImageToFile(
      attachments[i],
      `ai-image-${i}.png`,
      imageProxy
    );
    if (!file) continue;

    const blobId = await uploadBlob(host.std, file);
    store.addBlock(
      'affine:image',
      {
        height: 0,
        name: file.name,
        size: file.size,
        sourceId: blobId,
        type: file.type,
        width: 0,
      },
      noteId,
      index === undefined ? undefined : index + inserted
    );
    inserted++;
  }

  return { requested: attachments.length, inserted };
}

export async function constructRootChatBlockMessages(
  doc: Store,
  forkSessionId: string
) {
  // Convert chat messages to AI chat block messages
  const userInfo = await AIProvider.userInfo;
  const forkMessages = (await queryHistoryMessages(
    doc.workspace.id,
    forkSessionId,
    doc.id
  )) as ChatMessage[];
  return constructUserInfoWithMessages(forkMessages, userInfo);
}

function getViewportCenter(mode: DocMode, std: BlockStdScope) {
  const center = { x: 400, y: 50 };
  if (mode === 'page') {
    const viewport = std.get(EditPropsStore).getStorage('viewport');
    if (viewport) {
      if ('xywh' in viewport) {
        const bound = Bound.deserialize(viewport.xywh);
        center.x = bound.x + bound.w / 2;
        center.y = bound.y + bound.h / 2;
      } else {
        center.x = viewport.centerX;
        center.y = viewport.centerY;
      }
    }
  } else {
    // Else we should get latest viewport center from the edgeless root service
    const viewport = std.get(GfxControllerIdentifier).viewport;
    center.x = viewport.centerX;
    center.y = viewport.centerY;
  }

  return center;
}

function waitForNextEditorHost(
  previousHost: EditorHost,
  timeoutMs = 5000
): Promise<EditorHost | null> {
  const startedAt = Date.now();

  return new Promise(resolve => {
    const poll = () => {
      const nextHost = document.querySelector(
        'editor-host'
      ) as EditorHost | null;
      if (nextHost && nextHost !== previousHost) {
        resolve(nextHost);
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        resolve(null);
        return;
      }

      setTimeout(poll, 100);
    };

    poll();
  });
}

export function extractLatestChatTurn(messages: ChatMessage[]): ChatMessage[] {
  if (!messages.length) return [];

  let startIndex = 0;
  for (let index = messages.length - 1; index >= 0; index--) {
    const message = messages[index];
    if (
      message.role === 'user' &&
      message.messageType !== 'tool_approval_response'
    ) {
      startIndex = index;
      break;
    }
  }
  return messages.slice(startIndex);
}

const PORTABLE_CANVAS_HISTORY_LIMIT = 24;
/**
 * Converts collaborative display state into bounded, data-only chat history.
 * It preserves visible user text plus rendered assistant answers and source
 * summaries, but never imports confirmations, tool calls, execution authority,
 * or technical context references into visible conversation content.
 */
export function buildPortableCanvasTranscript(messages: ChatMessage[]) {
  return messages
    .flatMap(message => {
      if (
        (message.role !== 'user' && message.role !== 'assistant') ||
        message.messageType === 'tool_approval_request' ||
        message.messageType === 'tool_approval_response'
      ) {
        return [];
      }
      const rendered = (
        (message.streamObjects?.length
          ? mergeStreamContent(message.streamObjects)
          : '') || message.content
      ).trim();
      const content =
        message.role === 'assistant'
          ? buildReusableAssistantTurnMarkdown({
              markdown: rendered,
              streamObjects: message.streamObjects ?? [],
              sources: message.sources,
            })
          : rendered;
      const portableContent = content.trim();
      return portableContent
        ? [{ role: message.role, content: portableContent }]
        : [];
    })
    .slice(-PORTABLE_CANVAS_HISTORY_LIMIT);
}

// Add a single-turn AI chat block at the requested canvas position.
export function addAIChatBlock(
  host: EditorHost,
  messages: ChatMessage[],
  sessionId: string,
  viewportCenter: { x: number; y: number },
  index: string,
  options: {
    createdByUserId?: string;
    parentBlockId?: string;
  } = {}
) {
  const { store } = host;
  const surfaceBlock = store
    .getAllModels()
    .find(block => block.flavour === 'affine:surface');
  if (!surfaceBlock) {
    return;
  }

  const bound = getAIChatBlockBound(viewportCenter);
  const aiChatBlockId = store.addBlock(
    'affine:embed-ai-chat',
    {
      xywh: bound.serialize(),
      messages: JSON.stringify(messages),
      index,
      sessionId,
      rootWorkspaceId: store.workspace.id,
      rootDocId: store.id,
      parentBlockId: options.parentBlockId ?? '',
      createdByUserId:
        options.createdByUserId ??
        messages.find(message => message.role === 'user')?.userId ??
        '',
      canvasMode: 'agent',
      goalCapability: 'disabled',
      schemaVersion: 2,
    },
    surfaceBlock.id
  );

  return aiChatBlockId;
}

export function getAIChatBlockBound(center: { x: number; y: number }): Bound {
  return new Bound(
    center.x - AI_CHAT_BLOCK_WIDTH / 2,
    center.y - AI_CHAT_BLOCK_HEIGHT / 2,
    AI_CHAT_BLOCK_WIDTH,
    AI_CHAT_BLOCK_HEIGHT
  );
}

export function getAIChatBlockBoundRightOf(
  sourceBound: Bound,
  gap: number
): Bound {
  return new Bound(
    sourceBound.x + sourceBound.w + gap,
    sourceBound.y + sourceBound.h / 2 - AI_CHAT_BLOCK_HEIGHT / 2,
    AI_CHAT_BLOCK_WIDTH,
    AI_CHAT_BLOCK_HEIGHT
  );
}

export function promptDocTitle(host: EditorHost, autofill?: string) {
  const notification = host.std.getOptional(NotificationProvider);
  if (!notification) return Promise.resolve(undefined);

  return notification.prompt({
    title: 'Create linked doc',
    message: 'Enter a title for the new doc.',
    placeholder: 'Untitled',
    autofill,
    confirmText: 'Confirm',
    cancelText: 'Cancel',
  });
}

/**
 * Get insert below block based on current selections
 * @param host Editor host
 * @param currentSelections Current selections
 * @returns Selected blocks and selection state
 */
async function getInsertBelowBlock(
  host: EditorHost,
  currentSelections: Selections
): Promise<BlockComponent | null> {
  const currentTextSelection = currentSelections.text;
  const currentBlockSelections = currentSelections.blocks;
  const currentImageSelections = currentSelections.images;

  const [_, { selectedBlocks: blocks }] = host.command.exec(
    getSelectedBlocksCommand,
    {
      currentTextSelection,
      currentBlockSelections,
      currentImageSelections,
    }
  );

  if (blocks && blocks.length) {
    return blocks[blocks.length - 1];
  }

  return null;
}

/**
 * Base handler for inserting content below the block
 * @param host Editor host
 * @param content Content to insert
 * @param block block
 * @returns Whether insertion was successful
 */
async function insertBelowBlock(
  host: EditorHost,
  content: string,
  block: BlockComponent | null
): Promise<boolean> {
  if (!block) return false;

  reportResponse('result:insert');
  await insertBelow(host, content, block);
  return true;
}

export const PAGE_INSERT = {
  icon: InsertBelowIcon({ width: '20px', height: '20px' }),
  title: 'Insert',
  titleKey: 'com.streamlain.ai.chat.action.insert',
  showWhen: (host: EditorHost) => {
    if (host.std.store.readonly$.value) {
      return false;
    }

    return true;
  },
  toast: 'Successfully inserted',
  toastKey: 'com.streamlain.ai.chat.action.insert.toast',
  handler: async (
    host: EditorHost,
    payload: AssistantTurnPayload,
    currentSelections: Selections
  ) => {
    const content = reusableMarkdown(payload);
    const block = await getInsertBelowBlock(host, currentSelections);

    const isNothingSelected = !block;

    // In page mode, if nothing is selected, use the last content block
    let targetBlock = block;
    if (isNothingSelected) {
      const [_, { firstBlock: noteBlock }] = host.command.exec(
        getFirstBlockCommand,
        {
          flavour: 'affine:note',
        }
      );

      const lastChild = noteBlock?.lastChild();
      targetBlock = lastChild ? host.std.view.getBlock(lastChild.id) : null;
    }

    const success = await insertBelowBlock(host, content, targetBlock);

    // Insert attachment images after the text content
    let attachmentResult: AttachmentInsertResult | null = null;
    if (success && payload.attachments.length && targetBlock) {
      // Find the last inserted block (text was inserted after targetBlock)
      const parent = targetBlock.model.parent;
      const lastChild = parent?.lastChild();
      if (lastChild) {
        attachmentResult = await insertAttachmentImages(
          host,
          payload.attachments,
          lastChild
        );
      }
    }

    return resultWithAttachmentStatus(
      success,
      attachmentResult,
      PAGE_INSERT.toast
    );
  },
};

export const EDGELESS_INSERT = {
  ...PAGE_INSERT,
  handler: async (
    host: EditorHost,
    payload: AssistantTurnPayload,
    currentSelections: Selections
  ): Promise<ChatActionResult> => {
    const content = reusableMarkdown(payload);
    const block = await getInsertBelowBlock(host, currentSelections);

    const isNothingSelected = !block;

    // In edgeless mode, handle special cases
    if (isNothingSelected) {
      const gfx = host.std.get(GfxControllerIdentifier);
      const selectedElements = gfx.selection.selectedElements;
      const isOnlyOneNoteSelected =
        selectedElements.length === 1 &&
        selectedElements[0] instanceof NoteBlockModel;

      if (isOnlyOneNoteSelected) {
        // Insert into selected note
        const [_, { lastBlock: lastBlockModel }] = host.command.exec(
          getLastBlockCommand,
          {
            root: selectedElements[0] as NoteBlockModel,
          }
        );

        const lastBlock = lastBlockModel
          ? host.std.view.getBlock(lastBlockModel.id)
          : null;

        const success = await insertBelowBlock(host, content, lastBlock);
        let attachmentResult: AttachmentInsertResult | null = null;
        if (success && payload.attachments.length && lastBlock) {
          const parent = lastBlock.model.parent;
          const lastChild = parent?.lastChild();
          if (lastChild) {
            attachmentResult = await insertAttachmentImages(
              host,
              payload.attachments,
              lastChild
            );
          }
        }
        return resultWithAttachmentStatus(
          success,
          attachmentResult,
          PAGE_INSERT.toast
        );
      } else {
        // Create a new note (attachments handled inside ADD_TO_EDGELESS_AS_NOTE)
        return ADD_TO_EDGELESS_AS_NOTE.handler(
          host,
          payload,
          currentSelections
        );
      }
    }

    const success = await insertBelowBlock(host, content, block);
    let attachmentResult: AttachmentInsertResult | null = null;
    if (success && payload.attachments.length) {
      const parent = block.model.parent;
      const lastChild = parent?.lastChild();
      if (lastChild) {
        attachmentResult = await insertAttachmentImages(
          host,
          payload.attachments,
          lastChild
        );
      }
    }
    return resultWithAttachmentStatus(
      success,
      attachmentResult,
      PAGE_INSERT.toast
    );
  },
};

const SAVE_AS_BLOCK: ChatAction = {
  icon: BlockIcon({ width: '20px', height: '20px' }),
  title: 'Save as block',
  titleKey: 'com.streamlain.ai.chat.action.save-as-block',
  toast: 'Successfully saved chat to a block',
  toastKey: 'com.streamlain.ai.chat.action.save-as-block.toast',
  showWhen: (host: EditorHost) => {
    if (host.std.store.readonly$.value) {
      return false;
    }
    return true;
  },
  handler: async (
    host: EditorHost,
    payload: AssistantTurnPayload,
    _currentSelections: Selections
  ) => {
    // The chat session id and the latest message id are required to fork the chat session
    const parentSessionId = payload.sessionId;
    if (!payload.messageId || !parentSessionId) {
      return false;
    }

    const notificationService = host.std.getOptional(NotificationProvider);
    const docModeService = host.std.get(DocModeProvider);
    const layer = host.std.get(GfxControllerIdentifier).layer;
    const curMode = docModeService.getEditorMode() || 'page';
    const viewportCenter = getViewportCenter(curMode, host.std);
    const newBlockIndex = layer.generateIndex();
    // If current mode is not edgeless, switch to edgeless mode first
    if (curMode !== 'edgeless') {
      // Set mode to edgeless
      docModeService.setEditorMode('edgeless' as DocMode);
      // Notify user to switch to edgeless mode
      notificationService?.notify({
        title: 'Save chat to a block',
        accent: 'info',
        message:
          'This feature is not available in the page editor. Switch to edgeless mode.',
        onClose: function (): void {},
      });
    }

    try {
      const newSessionId = await AIProvider.forkChat?.({
        workspaceId: host.store.workspace.id,
        docId: host.store.id,
        sessionId: parentSessionId,
        latestMessageId: payload.messageId,
      });

      if (!newSessionId) {
        return false;
      }

      // Get messages before the latest message
      const messages = extractLatestChatTurn(
        await constructRootChatBlockMessages(host.store, newSessionId)
      );

      // After switching to edgeless mode, the user can save the chat to a block
      const blockId = addAIChatBlock(
        host,
        messages,
        newSessionId,
        viewportCenter,
        newBlockIndex
      );
      if (!blockId) {
        return false;
      }

      const telemetryService = host.std.getOptional(TelemetryProvider);
      telemetryService?.track('CanvasElementAdded', {
        control: 'manually save',
        page: 'whiteboard editor',
        module: 'ai chat panel',
        segment: 'right sidebar',
        type: 'chat block',
        category: 'root',
      });
      return true;
    } catch (err) {
      console.error(err);
      notificationService?.notify({
        title: 'Failed to save chat to a block',
        accent: 'error',
        onClose: function (): void {},
      });
      return false;
    }
  },
};

const ADD_TO_EDGELESS_AS_NOTE = {
  icon: EdgelessIcon({ width: '20px', height: '20px' }),
  title: 'Add to edgeless as note',
  titleKey: 'com.streamlain.ai.chat.action.add-to-canvas',
  showWhen: (host: EditorHost) => {
    if (host.std.store.readonly$.value) {
      return false;
    }
    return true;
  },
  toast: 'New note created',
  toastKey: 'com.streamlain.ai.chat.action.add-to-canvas.toast',
  handler: async (
    host: EditorHost,
    payload: AssistantTurnPayload,
    _currentSelections: Selections
  ): Promise<ChatActionResult> => {
    reportResponse('result:add-note');
    const { store } = host;
    const content = reusableMarkdown(payload);

    const gfx = host.std.get(GfxControllerIdentifier);
    const elements = gfx.selection.selectedElements;
    const props: { displayMode: NoteDisplayMode; xywh?: SerializedXYWH } = {
      displayMode: NoteDisplayMode.EdgelessOnly,
    };

    if (elements.length > 0) {
      // Position below selected elements
      const bound = getCommonBoundWithRotation(
        elements.map(e => Bound.deserialize(e.xywh))
      );
      const width = bound.w > 0 ? bound.w : DEFAULT_EDGELESS_NOTE_WIDTH;
      const newBound = new Bound(
        bound.x,
        bound.maxY + EDGELESS_NOTE_SELECTION_GAP,
        width,
        DEFAULT_EDGELESS_NOTE_HEIGHT
      );
      props.xywh = newBound.serialize();
    } else {
      // No selection: use viewport center (like SAVE_AS_BLOCK does)
      const viewportCenter = getViewportCenter('edgeless', host.std);
      const newBound = new Bound(
        viewportCenter.x - DEFAULT_EDGELESS_NOTE_WIDTH / 2,
        viewportCenter.y - DEFAULT_EDGELESS_NOTE_HEIGHT / 2,
        DEFAULT_EDGELESS_NOTE_WIDTH,
        DEFAULT_EDGELESS_NOTE_HEIGHT
      );
      props.xywh = newBound.serialize();
    }

    const id = store.addBlock('affine:note', props, store.root?.id);

    let attachmentResult: AttachmentInsertResult | null = null;
    if (payload.attachments.length) {
      attachmentResult = await insertImagesIntoNote(
        host,
        payload.attachments,
        store,
        id,
        0
      );
    }

    const markdownIndex = attachmentResult?.inserted ?? 0;
    if (content.trim()) {
      await insertFromMarkdown(host, content, store, id, markdownIndex);
    }

    gfx.selection.set({
      elements: [id],
      editing: false,
    });

    return resultWithAttachmentStatus(
      true,
      attachmentResult,
      'New note created'
    );
  },
};

export const SAVE_AS_DOC = {
  icon: PageIcon({ width: '20px', height: '20px' }),
  title: 'Save as doc',
  titleKey: 'com.streamlain.ai.chat.action.save-as-doc',
  showWhen: () => true,
  toast: 'New doc created',
  toastKey: 'com.streamlain.ai.chat.action.save-as-doc.toast',
  handler: async (
    host: EditorHost,
    payload: AssistantTurnPayload,
    _currentSelections: Selections
  ) => {
    reportResponse('result:add-page');
    const content = reusableMarkdown(payload);
    const doc = host.store.workspace.createDoc();
    const newDoc = doc.getStore();
    newDoc.load();
    const rootId = newDoc.addBlock('affine:page');
    newDoc.addBlock('affine:surface', {}, rootId);
    const noteId = newDoc.addBlock('affine:note', {}, rootId);

    host.std.getOptional(RefNodeSlotsProvider)?.docLinkClicked.next({
      pageId: newDoc.id,
      host,
    });
    const newHost = await waitForNextEditorHost(host);
    if (!newHost) {
      return {
        success: false,
        accent: 'error',
        toast: 'New doc created, but content could not be inserted',
      };
    }

    const { store } = newHost;
    await insertFromMarkdown(newHost, content, store, noteId, 0);

    let attachmentResult: AttachmentInsertResult | null = null;
    if (payload.attachments.length) {
      attachmentResult = await insertImagesIntoNote(
        newHost,
        payload.attachments,
        store,
        noteId
      );
    }

    return resultWithAttachmentStatus(
      true,
      attachmentResult,
      SAVE_AS_DOC.toast
    );
  },
};

const CREATE_AS_LINKED_DOC = {
  icon: LinkedPageIcon({ width: '20px', height: '20px' }),
  title: 'Create as a linked doc',
  showWhen: (host: EditorHost) => {
    if (host.std.store.readonly$.value) {
      return false;
    }
    return true;
  },
  toast: 'New doc created',
  handler: async (
    host: EditorHost,
    payload: AssistantTurnPayload,
    _currentSelections: Selections
  ) => {
    reportResponse('result:add-page');
    const content = reusableMarkdown(payload);

    const { store } = host;
    const surfaceBlock = store
      .getAllModels()
      .find(block => block.flavour === 'affine:surface');
    if (!surfaceBlock) {
      return false;
    }

    const docModeService = host.std.get(DocModeProvider);
    const mode = docModeService.getEditorMode();
    if (mode !== 'edgeless') {
      return false;
    }

    // Create a new doc and add the content to it
    const newDoc = host.store.workspace.createDoc().getStore();
    newDoc.load();
    const rootId = newDoc.addBlock('affine:page');
    newDoc.addBlock('affine:surface', {}, rootId);
    const noteId = newDoc.addBlock('affine:note', {}, rootId);
    await insertFromMarkdown(host, content, newDoc, noteId, 0);

    // Insert attachment images into the linked doc
    let attachmentResult: AttachmentInsertResult | null = null;
    if (payload.attachments.length) {
      attachmentResult = await insertImagesIntoNote(
        host,
        payload.attachments,
        newDoc,
        noteId
      );
    }

    const gfx = host.std.get(GfxControllerIdentifier);
    // Add a linked doc card to link to the new doc
    const elements = gfx.selection.selectedElements;
    const width = 364;
    const height = 390;
    let x = 0;
    let y = 0;
    if (elements.length) {
      // Calculate the bound of the selected elements first
      const bound = getCommonBoundWithRotation(
        elements.map(e => Bound.deserialize(e.xywh))
      );
      x = bound.x;
      y = bound.y + bound.h + 100;
    }

    // If the selected elements are not in the viewport, center the linked doc card
    if (x === Number.POSITIVE_INFINITY || y === Number.POSITIVE_INFINITY) {
      const viewportCenter = getViewportCenter(mode, host.std);
      x = viewportCenter.x - width / 2;
      y = viewportCenter.y - height / 2;
    }

    host.std.get(EdgelessCRUDIdentifier).addBlock(
      'affine:embed-linked-doc',
      {
        xywh: `[${x}, ${y}, ${width}, ${height}]`,
        style: 'vertical',
        pageId: newDoc.id,
      },
      surfaceBlock.id
    );

    return resultWithAttachmentStatus(
      true,
      attachmentResult,
      'New doc created'
    );
  },
};

export const PageEditorActions = [PAGE_INSERT, SAVE_AS_DOC, SAVE_AS_BLOCK];

export const EdgelessEditorActions = [
  EDGELESS_INSERT,
  ADD_TO_EDGELESS_AS_NOTE,
  SAVE_AS_DOC,
  SAVE_AS_BLOCK,
];

export const ChatBlockPeekViewActions = [
  ADD_TO_EDGELESS_AS_NOTE,
  CREATE_AS_LINKED_DOC,
];
